'use client';

import React, { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useGetCompanyRolesQuery } from 'src/api/redux';
import { Select, Option, Tooltip } from 'src/components/PulseUI';
import { useAuthorization } from 'src/hooks/authorization';
import {
  filterAssignableRoles,
  isCustomRole,
} from 'src/components/Settings/Companies/components/RolesAdmin/utils';
import { CustomRoleChip } from 'src/components/Settings/Companies/components/RolesAdmin/components/CustomRoleChip';

/** Shown to a viewer who can see the company but not change it. */
const READ_ONLY_LABEL = 'You do not have permission to assign roles';

interface RolesDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * @description Role picker for the create-user flow. The option values are role
 *              *codes*, which is what the API's `roles` payload resolves
 *              against — not role ids.
 * @returns React.ReactElement
 */
const RolesDropdown = ({ value, onChange }: RolesDropdownProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { userManagementCapabilities } = useAuthorization();
  const { canWrite, isStaff } = userManagementCapabilities;

  // Readable by MedScout staff and by License Managers, who hold
  // `read.company_roles.company` (MKT-429).
  const { data: roles } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId }
  );

  // Bulk Exporter is MedScout's to grant, so it is not offered to a customer
  // creating a user (MKT-527).
  const sortedRoles = useMemo(
    () =>
      filterAssignableRoles(roles?.results, isStaff).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    [roles?.results, isStaff]
  );

  return (
    <Tooltip
      content={READ_ONLY_LABEL}
      showOnCondition={!canWrite}
      className="w-full"
    >
      <Select
        size="md"
        value={value || null}
        onChange={(_event, newValue) => onChange(String(newValue))}
        placeholder={canWrite ? 'Select a role...' : READ_ONLY_LABEL}
        disabled={!canWrite}
        aria-label="User role"
        data-testid="user-role-select"
        className="rounded-full w-full"
      >
        {sortedRoles.map((role) => (
          <Option key={role.code} value={role.code}>
            {/* The label keeps its own element so it stays the option's text,
                chip or no chip. */}
            <span className="flex items-center gap-2">
              <span className="truncate">{role.label}</span>
              {isCustomRole(role) && (
                <CustomRoleChip
                  withTooltip={false}
                  data-testid={`custom-role-chip-${role.code}`}
                />
              )}
            </span>
          </Option>
        ))}
      </Select>
    </Tooltip>
  );
};

export default RolesDropdown;
